import React, { Context, createContext, FC, ReactNode, useEffect, useState } from "react";
import { THEME_LOCAL_STORAGE_KEY, ThemeSetting } from "../../hooks/useTheme";

export interface ThemeContextHolder {
    theme: ThemeSetting;
    resolvedTheme: "light" | "dark";
    setTheme: (theme: ThemeSetting) => void;
}

export const ThemeContext: Context<ThemeContextHolder> =
    createContext({
        theme: "dark",
    } as ThemeContextHolder);


interface Props {
    children?: ReactNode;
}

export const ThemeContextProvider: FC<Props> = ({
    children,
}) => {
    const [ theme, setTheme ] = useState<ThemeSetting>(() => {
        if (typeof window === "undefined") return "system";
        return (
            (localStorage.getItem(THEME_LOCAL_STORAGE_KEY) as ThemeSetting) ||
            "system"
        );
    });

    const [ resolvedTheme, setResolvedTheme ] = useState<"light" | "dark">(() => {
        if (typeof window === "undefined") return "light";
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        return mediaQuery.matches ? "dark" : "light";
    });


    useEffect(() => {
        const applyTheme = (newTheme: ThemeSetting) => {
            const root = window.document.documentElement;
            const resolved =
                newTheme === "system"
                    ? window.matchMedia("(prefers-color-scheme: dark)").matches
                        ? "dark"
                        : "light"
                    : newTheme;

            setResolvedTheme(resolved);
            root.classList.remove("light", "dark");
            root.classList.add(resolved);
        };

        applyTheme(theme);
        localStorage.setItem(THEME_LOCAL_STORAGE_KEY, theme);

        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handleChange = () => {
            if (theme === "system") {
                applyTheme("system");
            }
        };

        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
    }, [ theme ]);

    return (
        <>
            <ThemeContext
                value={{
                    theme,
                    resolvedTheme,
                    setTheme,
                }}
            >
                {children}
            </ThemeContext>
        </>
    );
};